import { fileIdOf, fileNamesOf, looseKey, parseAge, SEX_OPTIONS } from './samples'
import { candidateFitsWorkflow, candidateInfo, isCandidateAnalysed } from './candidates'
import { fastqSampleName } from 'src/utils/fastqNaming'

// ----------------------------------------------------------------------
// Sample sheet import: a hand-written CSV/TSV parser (no dependency; .xlsx goes
// through model/xlsx.js into the same rows), header auto-mapping with English and
// Turkish synonyms, value normalisers, and matching rows to the samples in the
// panel, then to uploads not in it yet. All pure.
// ----------------------------------------------------------------------

export const SHEET_FIELDS = [
  { value: 'sample', label: 'Sample ID' },
  { value: 'alias', label: 'Alias' },
  { value: 'sex', label: 'Sex' },
  { value: 'age', label: 'Age' },
  { value: 'onsetAge', label: 'Age at onset' },
  { value: 'ai', label: 'AI analysis' },
  { value: 'hpo', label: 'HPO terms' },
  { value: 'clinicalHistory', label: 'Clinical history' },
  { value: 'notes', label: 'Notes' },
  { value: 'normal', label: 'Normal sample' },
  { value: 'tumourType', label: 'Tumour type' },
]
export const SHEET_FIELD_LABEL = Object.fromEntries(SHEET_FIELDS.map((f) => [f.value, f.label]))

/**
 * The fields a sheet can fill for this kind of batch: germline takes phenotype
 * and, where AI can run, the AI choice; somatic takes the tumour type and, for
 * tumour-normal, the normal. Without flags, every field.
 */
export const sheetFieldsFor = ({ analysis, tumourNormal = false, ai = true } = {}) => {
  if (!analysis) return SHEET_FIELDS
  const somatic = analysis === 'somatic'
  const skip = somatic
    ? new Set(['onsetAge', 'ai', 'hpo', 'clinicalHistory', ...(tumourNormal ? [] : ['normal'])])
    : new Set(['normal', 'tumourType', ...(ai ? [] : ['ai'])])
  return SHEET_FIELDS.filter((f) => !skip.has(f.value))
}

// The sample ID of the template's example row. Its row is never imported.
export const EXAMPLE_SAMPLE_ID = 'EXAMPLE-01'

// Lower case, Turkish letters folded to ASCII, spaces and punctuation dropped.
export const normaliseHeader = (value) =>
  String(value || '')
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]/g, '')

const SYNONYMS = {
  sample: [
    'sample',
    'sampleid',
    'samplename',
    'sampleno',
    'id',
    'ornek',
    'ornekadi',
    'ornekid',
    'ornekno',
    'numune',
    'numuneno',
    'numuneadi',
    'numuneid',
    'hastano',
    'barkod',
    'barcode',
  ],
  alias: ['alias', 'samplealias', 'label', 'displayname', 'etiket', 'takmaad', 'kisaad'],
  sex: ['sex', 'gender', 'cinsiyet', 'karyotype', 'karyotip'],
  age: ['age', 'ageyears', 'yas', 'hastayasi'],
  onsetAge: [
    'onset',
    'onsetage',
    'ageatonset',
    'symptomsstartage',
    'startage',
    'baslangicyasi',
    'semptombaslangicyasi',
    'baslangic',
  ],
  ai: [
    'ai',
    'runai',
    'withai',
    'useai',
    'aianalysis',
    'aiassisted',
    'aiinterpretation',
    'interpretation',
    'yapayzeka',
    'yz',
    'yzanalizi',
    'yapayzekaanalizi',
    'aianalizi',
    'aidestekli',
  ],
  hpo: ['hpo', 'hpoterms', 'hpoterm', 'hpoids', 'hpoid', 'phenotype', 'phenotypes', 'fenotip'],
  clinicalHistory: [
    'clinicalhistory',
    'clinicalnotes',
    'clinicalinformation',
    'history',
    'klinikoyku',
    'klinikbilgi',
    'klinikbulgular',
    'anamnez',
    'oyku',
  ],
  notes: ['notes', 'note', 'comment', 'comments', 'not', 'notlar', 'aciklama'],
  normal: ['normal', 'normalsample', 'normalsampleid', 'matchednormal', 'normalornek', 'normalnumune'],
  tumourType: ['tumourtype', 'tumortype', 'oncotree', 'oncotreecode', 'tumortipi', 'tumorturu', 'kansertipi'],
}

const HEADER_TO_FIELD = new Map()
Object.entries(SYNONYMS).forEach(([field, words]) => words.forEach((w) => HEADER_TO_FIELD.set(w, field)))

export const fieldForHeader = (header) => HEADER_TO_FIELD.get(normaliseHeader(header)) || null

// ----------------------------------------------------------------------
// Parsing

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((l) => l.trim()) || '').replace(/"[^"]*"/g, '')
  if (firstLine.includes('\t')) return '\t'
  const commas = (firstLine.match(/,/g) || []).length
  const semis = (firstLine.match(/;/g) || []).length
  return semis > commas ? ';' : ','
}

/**
 * RFC 4180 style parser: quoted fields, "" escapes, CRLF or LF, a leading BOM.
 * Tab-separated text (pasted Excel cells, .tsv) uses the same rules.
 * @returns {string[][]} rows of cells, trailing empty rows dropped
 */
export const parseDelimited = (input, delimiter) => {
  const text = String(input || '').replace(/^﻿/, '')
  const sep = delimiter || detectDelimiter(text)
  const rows = []
  let row = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 1
        } else quoted = false
      } else cell += ch
    } else if (ch === '"' && cell === '') {
      quoted = true
    } else if (ch === sep) {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += ch
  }
  if (cell !== '' || row.length) {
    row.push(cell)
    rows.push(row)
  }
  while (rows.length && rows[rows.length - 1].every((c) => !String(c).trim())) rows.pop()
  return rows.map((r) => r.map((c) => String(c).trim()))
}

/**
 * Reads a sheet, as text (CSV, TSV, pasted cells) or as rows of cells (a worksheet
 * from model/xlsx.js): finds the header row (the first of the first 10 rows with 2
 * or more known headers, else the first row) and auto-maps its columns.
 * @returns {{ headers: string[], rows: string[][], mapping: (string|null)[], headerRow: number }}
 */
export const readSheet = (input) => {
  const all = Array.isArray(input)
    ? input.map((r) => (r || []).map((c) => String(c ?? '').trim()))
    : parseDelimited(input)
  while (all.length && all[0].every((c) => !c)) all.shift()
  if (!all.length) return { headers: [], rows: [], mapping: [], headerRow: 0 }
  let headerRow = 0
  for (let i = 0; i < Math.min(all.length, 10); i += 1) {
    const known = all[i].filter((h) => fieldForHeader(h)).length
    if (known >= 2) {
      headerRow = i
      break
    }
  }
  const headers = all[headerRow].map((h, i) => h || `Column ${i + 1}`)
  const rows = all.slice(headerRow + 1).filter((r) => r.some((c) => c))
  const used = new Set()
  const mapping = headers.map((h) => {
    const field = fieldForHeader(h)
    if (!field || used.has(field)) return null
    used.add(field)
    return field
  })
  // A single unmapped column in a sheet without a sample column is most likely it.
  if (!used.has('sample') && headers.length === 1) mapping[0] = 'sample'
  return { headers, rows, mapping, headerRow }
}

// ----------------------------------------------------------------------
// Values

const KARYOTYPES = new Set(SEX_OPTIONS.map((o) => o.value).filter(Boolean))
const MALE = new Set(['m', 'male', 'man', 'e', 'erkek', 'bay', 'xy'])
const FEMALE = new Set(['f', 'female', 'woman', 'k', 'kadin', 'kiz', 'bayan', 'xx'])
const UNKNOWN = new Set(['', '?', 'unknown', 'u', 'na', 'n/a', '-', 'bilinmiyor', 'belirsiz', 'none'])

/** @returns {{ value } | { error }} */
export const normaliseSex = (raw) => {
  const s = String(raw || '').trim()
  const folded = normaliseHeader(s)
  if (UNKNOWN.has(s.toLowerCase()) || !folded) return { value: '' }
  if (MALE.has(folded)) return { value: 'XY' }
  if (FEMALE.has(folded)) return { value: 'XX' }
  const upper = s.toUpperCase().replace(/[^XY]/g, '')
  if (KARYOTYPES.has(upper) && upper.length === s.replace(/\s/g, '').length) return { value: upper }
  return { error: `Sex not recognised: ${s}` }
}

const YES = new Set(['yes', 'y', 'evet', 'e', '1', 'true', 'x', 'ai', 'on', 'var'])
const NO = new Set(['no', 'n', 'hayir', 'h', '0', 'false', 'off', 'yok', 'standard', 'standart'])

/**
 * Yes or no, in English or Turkish (E is Evet here, not Erkek). Blank is
 * undefined: the sample follows the batch.
 * @returns {{ value: boolean|undefined } | { error }}
 */
export const normaliseYesNo = (raw) => {
  const s = String(raw ?? '').trim()
  if (!s || s === '-') return { value: undefined }
  const folded = normaliseHeader(s)
  if (YES.has(folded)) return { value: true }
  if (NO.has(folded)) return { value: false }
  return { error: `Use Yes or No. The sheet says ${s}` }
}

export const normaliseAgeValue = (raw, label = 'Age') => {
  const parsed = parseAge(raw)
  if (parsed === '') return { value: '' }
  if (Number.isNaN(parsed)) return { error: `${label} must be a number between 0 and 120. The sheet says ${raw}` }
  return { value: parsed }
}

/**
 * HPO cell: IDs separated by ; , | or new lines. "HP:1250" and "HP 0001250" are
 * accepted and padded. Free text is reported, not guessed.
 * @returns {{ ids: string[], unknown: string[] }}
 */
export const parseHpoCell = (raw) => {
  const ids = []
  const unknown = []
  String(raw || '')
    .split(/[;,|\n]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((token) => {
      const m = token.match(/^HP\s*[:_]?\s*(\d{1,7})(?:\s.*)?$/i)
      if (m) {
        const id = `HP:${m[1].padStart(7, '0')}`
        if (!ids.includes(id)) ids.push(id)
      } else unknown.push(token)
    })
  return { ids, unknown }
}

// ----------------------------------------------------------------------
// Matching rows to samples: exact sample name, then case- and separator-
// insensitive, then the sample name derived from each file name.

const sampleAliases = (sample) => {
  const keys = new Set([looseKey(sample.key)])
  ;(sample.files || []).forEach((name) => {
    keys.add(looseKey(name))
    keys.add(looseKey(fastqSampleName(name)))
  })
  return keys
}

const candidateAliases = (candidate) =>
  sampleAliases({ key: candidate.key, files: (candidate.rows || []).flatMap(fileNamesOf) })

const indexBy = (items, keyOf, aliasesOf) => {
  const exact = new Map()
  const loose = new Map()
  items.forEach((item) => {
    const key = String(keyOf(item))
    if (!exact.has(key)) exact.set(key, item)
    aliasesOf(item).forEach((k) => {
      if (k && !loose.has(k)) loose.set(k, item)
    })
  })
  return { exact, loose }
}

/**
 * The uploads a sheet row can bring into the batch: they fit the batch's analysis
 * and input, can be analysed (verified or verifying, not R1 only, not stuck) and
 * are not in the panel already. Uploads without an analysis come first, then the
 * newest, so a row picks those when a sample was uploaded twice.
 */
export const sheetUploads = (candidates = [], { workflow, analysed = new Set(), samples = [], now } = {}) => {
  const inPanel = new Set(samples.flatMap((s) => (s.fileIds || []).map(String)))
  return candidates
    .filter(
      (c) =>
        candidateFitsWorkflow(c, workflow) &&
        !candidateInfo(c, { now }).disabledReason &&
        !(c.rows || []).some((row) => inPanel.has(String(fileIdOf(row)))),
    )
    .map((c) => ({ c, analysed: isCandidateAnalysed(c, analysed), at: candidateInfo(c, { now }).uploadedAt }))
    .sort((a, b) => Number(a.analysed) - Number(b.analysed) || b.at - a.at)
    .map(({ c }) => c)
}

/**
 * Matches sheet rows to the samples in the panel (exact sample name, then case-
 * and separator-insensitive, then the name derived from each file name), then the
 * rest to uploads not in the panel yet (see sheetUploads), the same way. The
 * template's example row is skipped.
 * @returns {{ matches: Array<{ rowIndex, uid, how }>, additions: Array<{ rowIndex, candidate }>,
 *   unmatchedRows: Array<{ rowIndex, id }>, samplesWithoutRow: string[], duplicateIds: string[] }}
 */
export const matchRows = (rows, sampleCol, samples, uploads = []) => {
  const matches = []
  const additions = []
  const unmatchedRows = []
  const duplicateIds = []
  const seenIds = new Set()
  const taken = new Set()
  const takenUploads = new Set()
  const inPanel = indexBy(samples, (s) => s.key, sampleAliases)
  const byUpload = indexBy(uploads, (c) => c.key, candidateAliases)
  rows.forEach((row, rowIndex) => {
    const id = String(row[sampleCol] || '').trim()
    if (!id || id === EXAMPLE_SAMPLE_ID) return
    const loose = looseKey(id)
    if (seenIds.has(loose)) {
      duplicateIds.push(id)
      return
    }
    seenIds.add(loose)
    let sample = inPanel.exact.get(id)
    let how = 'id'
    if (!sample) {
      sample = inPanel.loose.get(loose)
      how = 'loose'
    }
    if (sample && !taken.has(sample.uid)) {
      taken.add(sample.uid)
      matches.push({ rowIndex, uid: sample.uid, how })
      return
    }
    const upload = sample ? null : byUpload.exact.get(id) || byUpload.loose.get(loose)
    if (upload && !takenUploads.has(upload.id)) {
      takenUploads.add(upload.id)
      additions.push({ rowIndex, candidate: upload })
    } else unmatchedRows.push({ rowIndex, id })
  })
  const samplesWithoutRow = samples.filter((s) => !taken.has(s.uid)).map((s) => s.key)
  return { matches, additions, unmatchedRows, samplesWithoutRow, duplicateIds }
}

/**
 * The samples after the sheet's uploads joined them: each addition's values go to
 * the sample that holds its files. Samples without one are returned as they are.
 */
export const withSheetAdditions = (samples, additions = []) => {
  if (!additions.length) return samples
  const patchOfFile = new Map()
  additions.forEach(({ candidate, patch }) =>
    (candidate.rows || []).forEach((row) => {
      const id = fileIdOf(row)
      if (id) patchOfFile.set(String(id), patch)
    }),
  )
  return samples.map((s) => {
    const patch = (s.fileIds || []).map((id) => patchOfFile.get(String(id))).find(Boolean)
    return patch ? { ...s, ...patch } : s
  })
}

/**
 * Values of one row for the mapped fields, normalised. HPO names and tumour
 * types are resolved afterwards (they need the server).
 * @returns {{ values: object, errors: object }}
 */
export const rowValues = (row, mapping) => {
  const values = {}
  const errors = {}
  mapping.forEach((field, col) => {
    if (!field || field === 'sample') return
    const raw = String(row[col] || '').trim()
    if (!raw) return
    if (field === 'sex') {
      const r = normaliseSex(raw)
      if (r.error) errors.sex = r.error
      else values.sex = r.value
    } else if (field === 'age' || field === 'onsetAge') {
      const r = normaliseAgeValue(raw, field === 'age' ? 'Age' : 'Age at onset')
      if (r.error) errors[field] = r.error
      else if (r.value !== '') values[field] = r.value
    } else if (field === 'hpo') {
      const { ids, unknown } = parseHpoCell(raw)
      if (ids.length) values.hpoIds = ids
      if (unknown.length) errors.hpo = `HPO term not found: ${unknown.slice(0, 3).join(', ')}`
    } else if (field === 'ai') {
      const r = normaliseYesNo(raw)
      if (r.error) errors.ai = `AI analysis: ${r.error.charAt(0).toLowerCase()}${r.error.slice(1)}`
      else if (r.value !== undefined) values.ai = r.value
    } else if (field === 'normal') {
      values.normal = raw
    } else if (field === 'tumourType') {
      values.tumourType = raw
    } else {
      values[field] = raw
    }
  })
  return { values, errors }
}

// ----------------------------------------------------------------------
// Template

const csvCell = (value) => {
  const s = String(value ?? '')
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * The template's columns for this kind of batch (model/template.js adds widths,
 * dropdowns and help). `ai` is false where AI cannot run on the batch.
 */
export const templateColumns = ({ analysis, tumourNormal, ai = true }) =>
  analysis === 'somatic'
    ? ['sample_id', 'sex', 'age', 'tumour_type', ...(tumourNormal ? ['normal_sample'] : []), 'notes']
    : ['sample_id', 'sex', 'age', 'onset_age', ...(ai ? ['ai'] : []), 'hpo', 'clinical_history', 'notes']

/**
 * CSV text of the template. Each row is an array of values in column order, or a
 * sample, which fills the sample ID only.
 */
export const templateCsv = (columns, rows = []) => {
  const lines = [columns.join(',')]
  rows.forEach((row) => {
    const values = Array.isArray(row) ? row : [row.key]
    lines.push(columns.map((_c, i) => csvCell(values[i])).join(','))
  })
  return `${lines.join('\r\n')}\r\n`
}

export const downloadBlob = (filename, blob) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Safari starts the download after the click returns.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

export const downloadText = (filename, text, type = 'text/csv;charset=utf-8') =>
  // The BOM makes Excel read the file as UTF-8 (Turkish letters).
  downloadBlob(filename, new Blob([`\ufeff${text}`], { type }))

/**
 * How a chosen file is read: 'xlsx' (Excel workbook), 'text' (CSV, TSV, TXT),
 * 'xls' (the old Excel format, which cannot be read) or 'other' (ODS, Numbers).
 */
export const sheetFileKind = (fileName) => {
  const name = String(fileName || '').toLowerCase()
  if (/\.(xlsx|xlsm)$/.test(name)) return 'xlsx'
  if (/\.xls$/.test(name)) return 'xls'
  if (/\.(ods|numbers|xlsb)$/.test(name)) return 'other'
  return 'text'
}

// The file input's accept list.
export const SHEET_ACCEPT =
  '.xlsx,.xlsm,.csv,.tsv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/tab-separated-values'
